import { HeroIcons } from '../../lib/utils/heroIcons'
import { useTranslation } from 'next-i18next'
import { useEffect, useRef, useState } from 'react'
import useLocale from '../../hooks/useLocale'

type AvatarTone = 'indigo' | 'violet' | 'blue' | 'slate' | 'purple'

interface Testimonial {
  id: string
  author: string
  since: string
  quote: string
  initials: string
  tone: AvatarTone
}

const TOP_QUOTE_PREVIEW_LIMIT = 230
const BOTTOM_QUOTE_PREVIEW_LIMIT = 160
const CAROUSEL_QUOTE_PREVIEW_LIMIT = 175

const avatarToneClasses = (tone: AvatarTone) => {
  switch (tone) {
    case 'indigo':
      return 'bg-[#dbe1ff] text-[#3442af]'
    case 'violet':
      return 'bg-[#efe1ff] text-[#6b3fb1]'
    case 'blue':
      return 'bg-[#dbecff] text-[#2e5ea8]'
    case 'slate':
      return 'bg-[#e6eafb] text-[#46588c]'
    case 'purple':
      return 'bg-[#e9e4ff] text-[#5742b0]'
    default:
      return 'bg-[#e6eafb] text-[#46588c]'
  }
}

const getQuotePreview = (quote: string, limit: number) => {
  if (quote.length <= limit) {
    return quote
  }

  return `${quote.slice(0, limit).trimEnd()}…`
}

export default function TestimonialsSection() {
  const { t } = useTranslation('index')
  const { locale } = useLocale()
  const sectionRef = useRef<HTMLElement | null>(null)
  const carouselRailRef = useRef<HTMLDivElement | null>(null)
  const hasAnimatedRef = useRef(false)
  const [isInView, setIsInView] = useState(false)
  const [selectedTestimonial, setSelectedTestimonial] =
    useState<Testimonial | null>(null)
  const topTestimonials = t('landing.testimonials.top', {
    returnObjects: true,
  }) as Testimonial[]
  const bottomTestimonials = t('landing.testimonials.bottom', {
    returnObjects: true,
  }) as Testimonial[]
  const allTestimonials = [...topTestimonials, ...bottomTestimonials]
  const carouselTestimonials = [...allTestimonials, ...allTestimonials]
  const trustpilotHref =
    locale === 'fr'
      ? 'https://fr.trustpilot.com/review/app.shelters.finance'
      : process.env.NEXT_PUBLIC_TRUSTPILOT_PAGE ||
        'https://www.trustpilot.com/review/app.shelters.finance'

  useEffect(() => {
    const node = sectionRef.current
    if (!node) return

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0]
        if (!entry.isIntersecting || hasAnimatedRef.current) return

        hasAnimatedRef.current = true
        setIsInView(true)
        observer.disconnect()
      },
      {
        threshold: 0.18,
        rootMargin: '0px 0px -10% 0px',
      },
    )

    observer.observe(node)
    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    if (!selectedTestimonial) return

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setSelectedTestimonial(null)
      }
    }

    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    window.addEventListener('keydown', onKeyDown)

    return () => {
      document.body.style.overflow = previousOverflow
      window.removeEventListener('keydown', onKeyDown)
    }
  }, [selectedTestimonial])

  useEffect(() => {
    const rail = carouselRailRef.current
    if (!rail || selectedTestimonial) return

    let frameId = 0
    let lastTimestamp = performance.now()

    const scrollStep = (timestamp: number) => {
      const delta = timestamp - lastTimestamp
      lastTimestamp = timestamp

      const loopWidth = rail.scrollWidth / 2
      if (loopWidth > 0) {
        rail.scrollLeft += delta * 0.055

        if (rail.scrollLeft >= loopWidth) {
          rail.scrollLeft -= loopWidth
        }
      }

      frameId = window.requestAnimationFrame(scrollStep)
    }

    frameId = window.requestAnimationFrame(scrollStep)

    return () => window.cancelAnimationFrame(frameId)
  }, [selectedTestimonial])

  return (
    <section ref={sectionRef} className="">
      <div className="mx-auto w-full px-3 md:px-10 lg:px-16 xl:px-24 2xl:px-44">
        <div className="grid gap-10 lg:grid-cols-12 lg:gap-7">
          <div
            className={`transition-all duration-700 lg:col-span-4 ${
              isInView ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'
            }`}
          >
            <div className="flex h-full max-w-[380px] flex-col">
              <div>
                <p className="inline-flex items-center gap-2 rounded-full border border-[#d7dcf8] bg-[#e9ebfb] px-4 py-2 text-shelters-kicker font-medium text-[#424d99]">
                  <HeroIcons.StarIcon className="h-4 w-4 text-[#00b67a]" />
                  {t('landing.testimonials.badge')}
                </p>

                <h2 className="mt-6 px-3 text-shelters-h3 font-semibold leading-[1.12] text-[#111327] tablet:px-0">
                  {t('landing.testimonials.title.line1')} <br />
                  <span className="text-[#5a57d8]">
                    {t('landing.testimonials.title.highlight')}
                  </span>
                </h2>

                <p className="mt-4 max-w-sm px-3 text-shelters-body text-[#2a2e44]/90 tablet:px-0">
                  {t('landing.testimonials.intro')}
                </p>
              </div>

              <div className="lg:mt-auto">
                <div className="mt-8 rounded-inner-card border border-[#d9ddf4] bg-white p-4 shadow-[0_10px_24px_rgba(62,56,136,0.06)]">
                  <p className="text-shelters-lead font-medium text-[#15182f]">
                    {t('landing.testimonials.rating.label')}
                  </p>

                  <div className="mt-3 flex items-center gap-3">
                    <div className="flex items-center gap-1">
                      {Array.from({ length: 5 }).map((_, index) =>
                        index < 4 ? (
                          <span
                            key={`trustpilot-rating-${index}`}
                            className="inline-flex h-8 w-8 items-center justify-center rounded-[3px] bg-[#00b67a]"
                          >
                            <HeroIcons.StarIcon className="h-4 w-4 text-white" />
                          </span>
                        ) : (
                          <span
                            key={`trustpilot-rating-${index}`}
                            className="relative inline-flex h-8 w-8 items-center justify-center overflow-hidden rounded-[3px] bg-[#d8dbe9]"
                          >
                            <span
                              aria-hidden="true"
                              className="absolute inset-y-0 left-0 w-1/2 bg-[#00b67a]"
                            />
                            <HeroIcons.StarIcon className="relative h-4 w-4 text-white" />
                          </span>
                        ),
                      )}
                    </div>
                    <p className="text-[2rem] font-medium leading-none text-[#15182f]">
                      {t('landing.testimonials.rating.value')}
                      <span className="ml-1 text-shelters-lead text-[#555d86]">
                        {t('landing.testimonials.rating.outOf')}
                      </span>
                    </p>
                  </div>

                  <p className="mt-2 text-shelters-kicker text-[#6670a0]">
                    {t('landing.testimonials.rating.basedOn')}
                  </p>

                  <p className="mt-3 inline-flex items-center gap-1.5 text-shelters-body font-medium text-[#111327]">
                    <HeroIcons.StarIcon className="h-5 w-5 text-[#00b67a]" />
                    {t('landing.testimonials.rating.trustpilot')}
                  </p>
                </div>
                <a
                  href={trustpilotHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="ml-3 mt-6 inline-flex items-center gap-2 text-shelters-body font-medium text-[#3b46c3] tablet:ml-0"
                >
                  {t('landing.testimonials.rating.link')}
                  <HeroIcons.ArrowRightIcon className="h-4 w-4" />
                </a>
              </div>
            </div>
          </div>

          <div className="min-w-0 lg:col-span-8">
            <div className="relative -mx-3 overflow-hidden px-3 desktop:hidden">
              <div className="pointer-events-none absolute inset-y-0 left-0 z-10 w-10 bg-[linear-gradient(90deg,#f4f6ff_0%,rgba(244,246,255,0)_100%)]" />
              <div className="pointer-events-none absolute inset-y-0 right-0 z-10 w-10 bg-[linear-gradient(270deg,#f4f6ff_0%,rgba(244,246,255,0)_100%)]" />

              <div
                ref={carouselRailRef}
                className="flex gap-4 overflow-x-auto pb-4 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
                aria-label={t('landing.testimonials.carouselAria')}
              >
                {carouselTestimonials.map((testimonial, index) => {
                  const originalIndex = index % allTestimonials.length
                  const isDuplicate = index >= allTestimonials.length
                  const shouldShowFullButton =
                    testimonial.quote.length > CAROUSEL_QUOTE_PREVIEW_LIMIT

                  return (
                    <article
                      key={`${testimonial.id}-${index}`}
                      aria-hidden={isDuplicate}
                      className={`flex min-h-[286px] w-[min(82vw,360px)] shrink-0 flex-col rounded-card border border-[#d9def9] bg-white px-5 py-5 shadow-[0_12px_26px_rgba(62,56,136,0.08)] transition-all duration-700 tablet:w-[360px] desktop:w-[380px] ${
                        isInView
                          ? 'translate-y-0 opacity-100'
                          : 'translate-y-4 opacity-0'
                      }`}
                      style={{
                        transitionDelay: `${140 + originalIndex * 80}ms`,
                      }}
                    >
                      <div className="flex items-center gap-1 text-[#f4b51d]">
                        {Array.from({ length: 5 }).map((_, starIndex) => (
                          <HeroIcons.StarIcon
                            key={`${testimonial.id}-${index}-${starIndex}`}
                            className="h-4 w-4"
                          />
                        ))}
                      </div>

                      <div className="mt-4 min-h-[136px]">
                        <p className="text-shelters-body leading-[1.55] text-[#1d2238]">
                          {getQuotePreview(
                            testimonial.quote,
                            CAROUSEL_QUOTE_PREVIEW_LIMIT,
                          )}
                        </p>

                        {shouldShowFullButton && (
                          <button
                            type="button"
                            tabIndex={isDuplicate ? -1 : undefined}
                            onClick={() => setSelectedTestimonial(testimonial)}
                            className="mt-2 text-shelters-kicker font-medium text-[#3b46c3] hover:text-[#2f38aa]"
                          >
                            {t('landing.testimonials.readMore')}
                          </button>
                        )}
                      </div>

                      <div className="mt-auto flex items-center gap-3 pt-4">
                        <span
                          className={`inline-flex h-9 w-9 items-center justify-center rounded-full text-[0.78rem] font-medium ${avatarToneClasses(
                            testimonial.tone,
                          )}`}
                        >
                          {testimonial.initials}
                        </span>
                        <div>
                          <p className="text-shelters-body font-medium text-[#1a1f33]">
                            {testimonial.author}
                          </p>
                          <p className="text-shelters-kicker text-[#7b83af]">
                            {testimonial.since}
                          </p>
                        </div>
                      </div>
                    </article>
                  )
                })}
              </div>
            </div>

            <div className="hidden desktop:block">
              <div className="grid grid-cols-2 gap-4">
                {topTestimonials.map((testimonial, index) => (
                  <article
                    key={testimonial.id}
                    className={`flex h-full min-h-[258px] flex-col rounded-card border border-[#d9def9] bg-white px-5 py-5 shadow-[0_12px_26px_rgba(62,56,136,0.08)] transition-all duration-700 ${
                      isInView
                        ? 'translate-y-0 opacity-100'
                        : 'translate-y-4 opacity-0'
                    }`}
                    style={{ transitionDelay: `${140 + index * 90}ms` }}
                  >
                    <div className="flex items-center gap-1 text-[#f4b51d]">
                      {Array.from({ length: 5 }).map((_, starIndex) => (
                        <HeroIcons.StarIcon
                          key={`${testimonial.id}-${starIndex}`}
                          className="h-4 w-4"
                        />
                      ))}
                    </div>

                    <div className="mt-4 min-h-[112px]">
                      <p className="text-shelters-body leading-[1.55] text-[#1d2238]">
                        {getQuotePreview(
                          testimonial.quote,
                          TOP_QUOTE_PREVIEW_LIMIT,
                        )}
                      </p>

                      {testimonial.quote.length > TOP_QUOTE_PREVIEW_LIMIT && (
                        <button
                          type="button"
                          onClick={() => setSelectedTestimonial(testimonial)}
                          className="mt-2 text-shelters-kicker font-medium text-[#3b46c3] hover:text-[#2f38aa]"
                        >
                          {t('landing.testimonials.readMore')}
                        </button>
                      )}
                    </div>

                    <div className="mt-auto flex items-center gap-3 pt-4">
                      <span
                        className={`inline-flex h-9 w-9 items-center justify-center rounded-full text-[0.78rem] font-medium ${avatarToneClasses(
                          testimonial.tone,
                        )}`}
                      >
                        {testimonial.initials}
                      </span>
                      <div>
                        <p className="text-shelters-body font-medium text-[#1a1f33]">
                          {testimonial.author}
                        </p>
                        <p className="text-shelters-kicker text-[#7b83af]">
                          {testimonial.since}
                        </p>
                      </div>
                    </div>
                  </article>
                ))}
              </div>

              <div className="mt-4 grid grid-cols-3 gap-4">
                {bottomTestimonials.map((testimonial, index) => (
                  <article
                    key={testimonial.id}
                    className={`flex h-full min-h-[258px] flex-col rounded-card border border-[#d9def9] bg-white px-5 py-5 shadow-[0_12px_26px_rgba(62,56,136,0.08)] transition-all duration-700 ${
                      isInView
                        ? 'translate-y-0 opacity-100'
                        : 'translate-y-4 opacity-0'
                    }`}
                    style={{ transitionDelay: `${320 + index * 80}ms` }}
                  >
                    <div className="flex items-center gap-1 text-[#f4b51d]">
                      {Array.from({ length: 5 }).map((_, starIndex) => (
                        <HeroIcons.StarIcon
                          key={`${testimonial.id}-${starIndex}`}
                          className="h-4 w-4"
                        />
                      ))}
                    </div>

                    <div className="mt-4 min-h-[112px]">
                      <p className="text-shelters-body leading-[1.55] text-[#1d2238]">
                        {getQuotePreview(
                          testimonial.quote,
                          BOTTOM_QUOTE_PREVIEW_LIMIT,
                        )}
                      </p>

                      {testimonial.quote.length >
                        BOTTOM_QUOTE_PREVIEW_LIMIT && (
                        <button
                          type="button"
                          onClick={() => setSelectedTestimonial(testimonial)}
                          className="mt-2 text-shelters-kicker font-medium text-[#3b46c3] hover:text-[#2f38aa]"
                        >
                          {t('landing.testimonials.readMore')}
                        </button>
                      )}
                    </div>

                    <div className="mt-auto flex items-center gap-3 pt-4">
                      <span
                        className={`inline-flex h-9 w-9 items-center justify-center rounded-full text-[0.78rem] font-medium ${avatarToneClasses(
                          testimonial.tone,
                        )}`}
                      >
                        {testimonial.initials}
                      </span>
                      <div>
                        <p className="text-shelters-body font-medium text-[#1a1f33]">
                          {testimonial.author}
                        </p>
                        <p className="text-shelters-kicker text-[#7b83af]">
                          {testimonial.since}
                        </p>
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {selectedTestimonial && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <button
            type="button"
            aria-label={t('landing.testimonials.modal.closeWindowAria')}
            className="testimonial-modal-overlay bg-[#0f1233]/42 absolute inset-0 backdrop-blur-sm"
            onClick={() => setSelectedTestimonial(null)}
          />

          <div className="testimonial-modal-panel relative z-10 w-full max-w-2xl rounded-card border border-[#d9def9] bg-white p-6 shadow-[0_24px_60px_rgba(35,29,107,0.28)] md:p-8">
            <button
              type="button"
              aria-label={t('landing.testimonials.modal.closeAria')}
              onClick={() => setSelectedTestimonial(null)}
              className="absolute right-4 top-4 inline-flex h-9 w-9 items-center justify-center rounded-full border border-[#d6dbf7] bg-[#f3f5ff] text-[#4c57aa]"
            >
              <HeroIcons.XMarkIcon className="h-5 w-5" />
            </button>

            <p className="text-shelters-kicker font-medium text-[#3b46c3]">
              {t('landing.testimonials.modal.label')}
            </p>

            <div className="mt-3 flex items-center gap-1 text-[#f4b51d]">
              {Array.from({ length: 5 }).map((_, index) => (
                <HeroIcons.StarIcon
                  key={`modal-star-${index}`}
                  className="h-5 w-5"
                />
              ))}
              <span className="ml-2 text-shelters-body font-medium text-[#1a1f33]">
                {t('landing.testimonials.modal.rating')}
              </span>
            </div>

            <p className="mt-5 text-shelters-lead leading-[1.55] text-[#1d2238]">
              {selectedTestimonial.quote}
            </p>

            <div className="mt-7 flex items-center gap-3 border-t border-[#e1e6ff] pt-5">
              <span
                className={`inline-flex h-10 w-10 items-center justify-center rounded-full text-[0.82rem] font-medium ${avatarToneClasses(
                  selectedTestimonial.tone,
                )}`}
              >
                {selectedTestimonial.initials}
              </span>
              <div>
                <p className="text-shelters-body font-medium text-[#1a1f33]">
                  {selectedTestimonial.author}
                </p>
                <p className="text-shelters-kicker text-[#7b83af]">
                  {selectedTestimonial.since}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  )
}
